import { useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CardContainer from './layout/CardContainer';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import DateLabel from '../label/DateLabel';
import { numberToString } from '../special/numberConverter';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExoDialog from '../exo/ExoDialog';
import moment from 'moment';
import { renderHours } from '../../scenes/time-tracking/worktime';
import RenderHTML from '../preview/RenderHTML';

const TimeBillingCard = ({ timeTrackingData, timeTrackings = [], span }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!timeTrackingData || !timeTrackingData.billingConfig) {
    return null;
  }

  const { billingConfig, ids = [], adjustments = [] } = timeTrackingData;
  const { mode, startDate, endDate, wagePresets = [] } = billingConfig;

  // Helper to get adjustment for a time tracking
  // Adjustments is an array matching the order of ids
  const getAdjustment = (ttId) => {
    // If adjustments is an array, find by index in ids array
    if (Array.isArray(adjustments)) {
      const index = ids.findIndex((id) => id === ttId || String(id) === String(ttId));
      return index >= 0 ? adjustments[index] || {} : {};
    }
    // Fallback: if adjustments is an object with ID keys
    return adjustments[ttId] || adjustments[String(ttId)] || {};
  };

  // Helper to normalize adjustment keys (handles both camelCase and snake_case)
  const getAdjValue = (adj, key) => {
    const camelCase = key;
    const snakeCase = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    return adj[camelCase] !== undefined ? adj[camelCase] : adj[snakeCase];
  };

  // Calculate hours from timeTrackings if not stored in billingConfig
  // Returns { adjustedHours, originalHours, hasAdjustments }
  const calculateHoursFromTimeTrackings = (presetId, presetTimeTrackingIds) => {
    if (!timeTrackings || timeTrackings.length === 0) {
      return { adjustedHours: 0, originalHours: 0, hasAdjustments: false };
    }

    let totalAdjustedSeconds = 0;
    let totalOriginalSeconds = 0;
    let hasAdjustments = false;
    const ttIds = presetTimeTrackingIds || [];

    timeTrackings.forEach((tt) => {
      if (ttIds.includes(tt.id) || ttIds.includes(String(tt.id))) {
        const adj = getAdjustment(tt.id);
        const excludeFromReport = getAdjValue(adj, 'excludeFromReport');
        const timeStart = getAdjValue(adj, 'timeStart');
        const timeEnd = getAdjValue(adj, 'timeEnd');

        if (excludeFromReport) {
          hasAdjustments = true;
          // Add to original but not adjusted
          const originalStart = moment(tt.timeStart);
          const originalEnd = moment(tt.timeEnd);
          if (originalStart.isValid() && originalEnd.isValid()) {
            totalOriginalSeconds += originalEnd.diff(originalStart, 'seconds');
          }
          return;
        }

        const originalStart = moment(tt.timeStart);
        const originalEnd = moment(tt.timeEnd);
        const adjustedStart = moment(timeStart || tt.timeStart);
        const adjustedEnd = moment(timeEnd || tt.timeEnd);

        if (timeStart || timeEnd) {
          hasAdjustments = true;
        }

        if (originalStart.isValid() && originalEnd.isValid()) {
          totalOriginalSeconds += originalEnd.diff(originalStart, 'seconds');
        }
        if (adjustedStart.isValid() && adjustedEnd.isValid()) {
          totalAdjustedSeconds += adjustedEnd.diff(adjustedStart, 'seconds');
        }
      }
    });

    return {
      adjustedHours: totalAdjustedSeconds / 3600,
      originalHours: totalOriginalSeconds / 3600,
      hasAdjustments: hasAdjustments || totalAdjustedSeconds !== totalOriginalSeconds,
    };
  };

  // Get hours for a preset - returns { adjustedHours, originalHours, hasAdjustments }
  const getPresetHours = (preset) => {
    // If hours are stored, use them as adjustedHours
    if (preset.hours !== undefined && preset.hours !== null) {
      // Calculate original hours from time trackings to detect adjustments
      const calculated = calculateHoursFromTimeTrackings(preset.id, preset.timeTrackingIds);
      return {
        adjustedHours: preset.hours,
        originalHours: calculated.originalHours,
        hasAdjustments: calculated.hasAdjustments || Math.abs(preset.hours - calculated.originalHours) > 0.01,
      };
    }
    return calculateHoursFromTimeTrackings(preset.id, preset.timeTrackingIds);
  };

  // Get total hours and amount
  const getTotals = () => {
    if (mode === 'chr' && wagePresets.length > 0) {
      let totalAdjustedHours = 0;
      let totalOriginalHours = 0;
      let totalAmount = 0;
      let hasAdjustments = false;
      wagePresets.forEach((preset) => {
        const { adjustedHours, originalHours, hasAdjustments: presetHasAdj } = getPresetHours(preset);
        totalAdjustedHours += adjustedHours;
        totalOriginalHours += originalHours;
        totalAmount += adjustedHours * (preset.wage || 0);
        if (presetHasAdj) hasAdjustments = true;
      });
      return { totalAdjustedHours, totalOriginalHours, totalAmount, hasAdjustments };
    }
    // For positions mode, calculate from all time trackings
    if (mode === 'positions' && timeTrackings.length > 0) {
      let totalAdjustedSeconds = 0;
      let totalOriginalSeconds = 0;
      let hasAdjustments = false;
      timeTrackings.forEach((tt) => {
        const adj = getAdjustment(tt.id);
        const excludeFromReport = getAdjValue(adj, 'excludeFromReport');
        const timeStart = getAdjValue(adj, 'timeStart');
        const timeEnd = getAdjValue(adj, 'timeEnd');

        const originalStart = moment(tt.timeStart);
        const originalEnd = moment(tt.timeEnd);
        if (originalStart.isValid() && originalEnd.isValid()) {
          totalOriginalSeconds += originalEnd.diff(originalStart, 'seconds');
        }
        if (excludeFromReport) {
          hasAdjustments = true;
          return;
        }
        if (timeStart || timeEnd) hasAdjustments = true;
        const start = moment(timeStart || tt.timeStart);
        const end = moment(timeEnd || tt.timeEnd);
        if (start.isValid() && end.isValid()) {
          totalAdjustedSeconds += end.diff(start, 'seconds');
        }
      });
      return {
        totalAdjustedHours: totalAdjustedSeconds / 3600,
        totalOriginalHours: totalOriginalSeconds / 3600,
        totalAmount: 0,
        hasAdjustments: hasAdjustments || totalAdjustedSeconds !== totalOriginalSeconds,
      };
    }
    return null;
  };

  const totals = getTotals();

  // Build preset map for dialog
  const presetByTimeTrackingId = {};
  wagePresets.forEach((preset) => {
    (preset.timeTrackingIds || []).forEach((ttId) => {
      presetByTimeTrackingId[ttId] = preset;
      presetByTimeTrackingId[String(ttId)] = preset;
    });
  });

  return (
    <>
      <CardContainer span={span}>
        {/* Header */}
        <Box className="w-full flex flex-row justify-between items-center">
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {t('Time Billing')}
          </Typography>
          <Box className="flex flex-row items-center gap-1">
            <Typography variant="caption" sx={{ color: colors.grey[500] }}>
              {mode === 'chr' ? t('Hourly Rates') : t('Positions')}
            </Typography>
            {timeTrackings.length > 0 && (
              <IconButton size="small" onClick={() => setDialogOpen(true)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Date Range */}
        <Box
          className="w-full flex flex-row gap-1"
          sx={{ color: colors.grey[500] }}
        >
          <Typography variant="body2">
            <DateLabel date={startDate} />
          </Typography>
          <Typography variant="body2">-</Typography>
          <Typography variant="body2">
            <DateLabel date={endDate} />
          </Typography>
        </Box>

        {/* CHR Mode: Show wage presets */}
        {mode === 'chr' && wagePresets.length > 0 && (
          <Box className="w-full flex flex-col gap-1 mt-1">
            {wagePresets.map((preset, index) => {
              const { adjustedHours, originalHours, hasAdjustments } = getPresetHours(preset);
              return (
                <Box
                  key={preset.id || index}
                  className="flex flex-row justify-between items-center"
                >
                  <Typography variant="body2" sx={{ color: colors.grey[600] }}>
                    {preset.label}
                  </Typography>
                  <Box className="flex flex-col items-end">
                    {hasAdjustments && (
                      <Typography
                        variant="body2"
                        className="monospace"
                        sx={{ textDecoration: 'line-through', opacity: 0.5 }}
                      >
                        {numberToString(originalHours)} h
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      className="monospace"
                      sx={hasAdjustments ? { color: colors.success } : {}}
                    >
                      {numberToString(adjustedHours)} h
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            {/* Totals - only show if multiple presets */}
            {wagePresets.length > 1 && totals && (
              <Box
                className="flex flex-row justify-between items-center pt-1 mt-1"
                sx={{ borderTop: `1px solid ${colors.grey[800]}` }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('Total')}
                </Typography>
                <Box className="flex flex-col items-end">
                  {totals.hasAdjustments && (
                    <Typography
                      variant="body2"
                      className="monospace"
                      sx={{ fontWeight: 500, textDecoration: 'line-through', opacity: 0.5 }}
                    >
                      {numberToString(totals.totalOriginalHours)} h
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    className="monospace"
                    sx={{ fontWeight: 500, ...(totals.hasAdjustments ? { color: colors.success } : {}) }}
                  >
                    {numberToString(totals.totalAdjustedHours)} h
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Positions Mode: Show summary */}
        {mode === 'positions' && (
          <Box className="w-full flex flex-col gap-1 mt-1">
            <Box className="flex flex-row justify-between items-center">
              <Typography variant="body2" sx={{ color: colors.grey[600] }}>
                {t('Time Entries')}
              </Typography>
              <Typography variant="body2" className="monospace">
                {ids.length}
              </Typography>
            </Box>
            {totals && totals.totalAdjustedHours > 0 && (
              <Box className="flex flex-row justify-between items-center">
                <Typography variant="body2" sx={{ color: colors.grey[600] }}>
                  {t('Total')}
                </Typography>
                <Box className="flex flex-col items-end">
                  {totals.hasAdjustments && (
                    <Typography
                      variant="body2"
                      className="monospace"
                      sx={{ textDecoration: 'line-through', opacity: 0.5 }}
                    >
                      {numberToString(totals.totalOriginalHours)} h
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    className="monospace"
                    sx={totals.hasAdjustments ? { color: colors.success } : {}}
                  >
                    {numberToString(totals.totalAdjustedHours)} h
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContainer>

      {/* Time Entries Dialog */}
      <ExoDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        limitWidth
        maxWidth="1000px"
      >
        <Box
          className="h-full flex flex-col gap-4 p-4 rounded-lg"
          sx={{ backgroundColor: colors.card }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('Billed Time Entries')}
          </Typography>

          <Box className="flex flex-row gap-2" sx={{ color: colors.grey[500] }}>
            <Typography variant="body2">
              <DateLabel date={startDate} />
            </Typography>
            <Typography variant="body2">-</Typography>
            <Typography variant="body2">
              <DateLabel date={endDate} />
            </Typography>
          </Box>

          <TableContainer
            component={Box}
            sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('Date')}</TableCell>
                  <TableCell>{t('Employee')}</TableCell>
                  <TableCell>{t('Start')}</TableCell>
                  <TableCell>{t('End')}</TableCell>
                  <TableCell align="right">{t('Duration')}</TableCell>
                  {mode === 'chr' && (
                    <TableCell>{t('Hourly Rate')}</TableCell>
                  )}
                  <TableCell>{t('Comment')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeTrackings.map((tt) => {
                  const adj = getAdjustment(tt.id);
                  const excludeFromReport = getAdjValue(adj, 'excludeFromReport');
                  const timeStart = getAdjValue(adj, 'timeStart');
                  const timeEnd = getAdjValue(adj, 'timeEnd');
                  const adjComment = adj.comment;

                  if (excludeFromReport) return null;

                  const originalStart = moment(tt.timeStart);
                  const originalEnd = moment(tt.timeEnd);
                  const originalDurationMinutes = originalEnd.diff(originalStart, 'minutes');

                  const hasTimeAdjustment = timeStart || timeEnd;
                  const hasCommentAdjustment = adjComment && adjComment !== tt.comment;

                  const adjustedStart = timeStart ? moment(timeStart) : originalStart;
                  const adjustedEnd = timeEnd ? moment(timeEnd) : originalEnd;
                  const adjustedDurationMinutes = adjustedEnd.diff(adjustedStart, 'minutes');

                  const preset = presetByTimeTrackingId[tt.id] || presetByTimeTrackingId[String(tt.id)];

                  return (
                    <TableRow key={tt.id}>
                      <TableCell>
                        <DateLabel date={originalStart} />
                      </TableCell>
                      <TableCell>
                        {tt.employee
                          ? `${tt.employee.firstName} ${tt.employee.lastName}`
                          : '-'}
                      </TableCell>
                      <TableCell className="monospace">
                        <Box className="flex flex-col">
                          <span style={{ textDecoration: hasTimeAdjustment ? 'line-through' : 'none', opacity: hasTimeAdjustment ? 0.5 : 1 }}>
                            {originalStart.format('HH:mm')}
                          </span>
                          {hasTimeAdjustment && (
                            <span style={{ color: colors.success }}>
                              {adjustedStart.format('HH:mm')}
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className="monospace">
                        <Box className="flex flex-col">
                          <span style={{ textDecoration: hasTimeAdjustment ? 'line-through' : 'none', opacity: hasTimeAdjustment ? 0.5 : 1 }}>
                            {originalEnd.format('HH:mm')}
                          </span>
                          {hasTimeAdjustment && (
                            <span style={{ color: colors.success }}>
                              {adjustedEnd.format('HH:mm')}
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" className="monospace">
                        <Box className="flex flex-col items-end">
                          <span style={{ textDecoration: hasTimeAdjustment ? 'line-through' : 'none', opacity: hasTimeAdjustment ? 0.5 : 1 }}>
                            {renderHours(originalDurationMinutes)}
                          </span>
                          {hasTimeAdjustment && (
                            <span style={{ color: colors.success }}>
                              {renderHours(adjustedDurationMinutes)}
                            </span>
                          )}
                        </Box>
                      </TableCell>
                      {mode === 'chr' && (
                        <TableCell>
                          {preset
                            ? `${preset.label} (${numberToString(preset.wage)} €/h)`
                            : '-'}
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          maxWidth: 300,
                        }}
                      >
                        <Box className="flex flex-col">
                          {hasCommentAdjustment ? (
                            <>
                              <Box sx={{ textDecoration: 'line-through', opacity: 0.5 }}>
                                {tt.comment ? <RenderHTML HTML={tt.comment} /> : '-'}
                              </Box>
                              <Box sx={{ color: colors.success }}>
                                <RenderHTML HTML={adjComment} />
                              </Box>
                            </>
                          ) : (
                            <Box>
                              {(adjComment || tt.comment) ? (
                                <RenderHTML HTML={adjComment || tt.comment} />
                              ) : '-'}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals */}
          {totals && (
            <Box
              className="flex flex-row justify-end gap-4 pt-2"
              sx={{ borderTop: `1px solid ${colors.grey[700]}` }}
            >
              <Box className="flex flex-row items-center gap-2">
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t('Total')}:
                </Typography>
                {totals.hasAdjustments && (
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, textDecoration: 'line-through', opacity: 0.5 }}
                  >
                    {numberToString(totals.totalOriginalHours)} h
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, ...(totals.hasAdjustments ? { color: colors.success } : {}) }}
                >
                  {numberToString(totals.totalAdjustedHours)} h
                </Typography>
              </Box>
              {mode === 'chr' && totals.totalAmount > 0 && (
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {numberToString(totals.totalAmount)} €
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </ExoDialog>
    </>
  );
};

export default TimeBillingCard;
